/**
 * QA Panel — i18next post-processor.
 *
 * Handles two display modes that mutate every translated string at the
 * i18next layer, so no component anywhere in the app needs to know about it.
 *
 *   - 'pseudoLoc'  → Sεττïñgs ~~~ (visual marker + length expansion)
 *                    Reveals hardcoded strings (they don't get the marker)
 *                    AND layout breaks from longer translations.
 *
 *   - 'showKeys'   → [settings.title] (replaces value with the i18n key)
 *                    Lets QA match on-screen text to JSON keys instantly.
 *
 *   - 'normal'     → passthrough.
 *
 * The post-processor is registered once at panel init. Mode is held in a
 * shared mutable state object so toggles from the popup are immediate
 * (after re-emitting i18next's languageChanged event to force re-render).
 */

import i18next from '../../../../i18n';
import type { QaMode } from '../types';

interface QaPostProcessorState {
    mode: QaMode;
}

/** Module-scoped mode flag, mutated by setQaMode below. */
const state: QaPostProcessorState = { mode: 'normal' };

/* ── Pseudo-localization ─────────────────────────────────────────── */

const PSEUDO_MAP: Record<string, string> = {
    a: 'á', b: 'ƀ', c: 'ç', d: 'đ', e: 'ë', f: 'ƒ', g: 'ǵ', h: 'ĥ',
    i: 'ï', j: 'ĵ', k: 'ǩ', l: 'ł', m: 'ɱ', n: 'ñ', o: 'ö', p: 'ƥ',
    q: 'ǫ', r: 'ŕ', s: 'ş', t: 'ţ', u: 'ü', v: 'ṽ', w: 'ŵ', x: 'ẋ',
    y: 'ÿ', z: 'ž',
    A: 'Á', B: 'Ɓ', C: 'Ç', D: 'Đ', E: 'Ë', F: 'Ƒ', G: 'Ǵ', H: 'Ĥ',
    I: 'Ï', J: 'Ĵ', K: 'Ǩ', L: 'Ł', M: 'Ḿ', N: 'Ñ', O: 'Ö', P: 'Ƥ',
    Q: 'Ǫ', R: 'Ŕ', S: 'Ş', T: 'Ţ', U: 'Ü', V: 'Ṽ', W: 'Ŵ', X: 'Ẋ',
    Y: 'Ÿ', Z: 'Ž',
};

/** Pad ratio: simulate German/Russian length expansion. 1.4 = 40% longer. */
const PADDING_RATIO = 1.4;
const PAD_CHAR = '~';
const MARKER = '‼';

function pseudoLocalize(value: string): string {
    // Don't transform interpolation placeholders like {{name}} or {count}
    // Split on the placeholder regex and only transform the literal segments.
    const parts = value.split(/(\{\{[^}]+\}\}|\{[^}]+\})/g);
    const transformed = parts
        .map((part) => {
            if (/^\{\{?[^}]+\}\}?$/.test(part)) return part;
            let out = '';
            for (const ch of part) {
                out += PSEUDO_MAP[ch] ?? ch;
            }
            return out;
        })
        .join('');

    const targetLength = Math.ceil(value.length * PADDING_RATIO);
    const padNeeded = Math.max(0, targetLength - transformed.length);
    const padding = padNeeded > 0 ? ` ${PAD_CHAR.repeat(Math.min(padNeeded, 30))}` : '';

    return `${MARKER}${transformed}${padding}${MARKER}`;
}

/* ── Public API ──────────────────────────────────────────────────── */

let registered = false;

/**
 * Register the post-processor with i18next. Idempotent — safe to call
 * multiple times. Activates the global postProcess option so every t() call
 * runs through it; the processor itself short-circuits to passthrough when
 * mode === 'normal' (zero overhead in the default state).
 */
export function registerQaPostProcessor(): void {
    if (registered) return;
    registered = true;

    i18next.use({
        type: 'postProcessor',
        name: 'qaPanel',
        process(value: unknown, key: string | string[]): unknown {
            if (state.mode === 'normal') return value;
            if (typeof value !== 'string') return value;

            if (state.mode === 'showKeys') {
                const keyStr = Array.isArray(key) ? key.join(',') : key;
                return `[${keyStr}]`;
            }

            // pseudoLoc
            return pseudoLocalize(value);
        },
    });

    // Enable globally so we don't need to update every t() call site.
    const existing = i18next.options.postProcess;
    if (Array.isArray(existing)) {
        if (!existing.includes('qaPanel')) existing.push('qaPanel');
    } else if (typeof existing === 'string') {
        i18next.options.postProcess = [existing, 'qaPanel'];
    } else {
        i18next.options.postProcess = ['qaPanel'];
    }
}

/**
 * Switch QA display mode and force all React components using
 * useTranslation() to re-render so the new processing takes effect.
 */
export function setQaMode(mode: QaMode): void {
    state.mode = mode;
    // react-i18next listens for languageChanged to trigger re-renders.
    // Re-emitting it without actually changing the language is the standard
    // trick to force every t() call to re-run through the post-processor.
    i18next.emit('languageChanged', i18next.language);
}

export function getQaMode(): QaMode {
    return state.mode;
}
